import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import PullToRefresh from "react-simple-pull-to-refresh";
import { useState, useMemo, useRef, useEffect } from "react";
import dayjs from "dayjs";
import { Collapse } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "app/store";
import { Spin, UserAvatar } from "common/elements";
import i18n from "features/shared/i18n";
import {
  sendChatRoomMessage,
  getParentChatRoomMessages,
} from "features/message/api";
import {
  MessageDate,
  ChatMessageItem,
  OfficialChatMessageItem,
} from "features/message/components";
import {
  ChatRoomMemberType,
  ChatRoomType,
  MessageContentType,
  MessageType,
} from "features/message/types";
import {
  messageActions,
  selectAllParentChatRooms,
  selectAllParentChatRoomMessages,
  selectParentChatRoomMessageSyncById,
  selectAllParentChatRoomMessageSync,
} from "features/message/slice";
import { ParentChatInputBar } from "./components";

export const MessagePage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [refresh, setRefresh] = useState(false);
  const chatRooms = useSelector(selectAllParentChatRooms);
  const allMessages = useSelector(selectAllParentChatRoomMessages);
  const users = useSelector((state: RootState) => state.user.users);
  const parent = useSelector((state: RootState) => state.user.parent);
  const isRefreshed = useSelector(
    (state: RootState) => state.message.isRefreshed,
  );
  const allParentChatRoomMessageSync = useSelector(
    selectAllParentChatRoomMessageSync,
  );
  const selectedChatRoomId = useSelector(
    (state: RootState) => state.message.selectedChatRoomId,
  );
  const chatRoomMessageSync = useSelector((state: RootState) =>
    selectedChatRoomId
      ? selectParentChatRoomMessageSyncById(state, selectedChatRoomId)
      : null,
  );

  const parentChatRoomLookUp = Array.from(allParentChatRoomMessageSync).reduce(
    (acc, sync) =>
      Object.assign(acc, { [sync.id]: sync.latestMessage?.created_at }),
    {},
  );

  const sortedParentChatRoom = chatRooms
    .filter((room) => parentChatRoomLookUp[room.id])
    .sort((a, b) =>
      dayjs(parentChatRoomLookUp[a.id], "YYYY-MM-DDTHH:mm:ssZ").diff(
        dayjs(parentChatRoomLookUp[b.id], "YYYY-MM-DDTHH:mm:ssZ"),
      ) > 0
        ? -1
        : 1,
    );

  useEffect(() => {
    if (sortedParentChatRoom.length > 0) {
      dispatch(
        messageActions.setSelectedChatRoomId(sortedParentChatRoom[0].id),
      );
    }
    return () => {
      dispatch(messageActions.setSelectedChatRoomId(null));
    };
  }, []);

  const selectedRoom = useMemo(() => {
    return chatRooms.find((room) => room.id === selectedChatRoomId);
  }, [chatRooms, selectedChatRoomId]);

  const messages = useMemo(() => {
    return allMessages
      .filter((msg) => msg.chat_room_id === selectedChatRoomId)
      .sort((a, b) =>
        dayjs(a.created_at).isBefore(dayjs(b.created_at)) ? -1 : 1,
      );
  }, [allMessages, selectedChatRoomId]);

  const getChildUser = (room: typeof selectedRoom) => {
    if (!room) return null;
    const kidsMember = room.members?.find(
      (m) => m.type === ChatRoomMemberType.KIDS,
    );
    if (!kidsMember) return null;
    return users.find((u) => u.uid === kidsMember.uid);
  };

  useEffect(() => {
    if (scrollRef.current) {
      if (isRefreshed) {
        scrollRef.current.scrollTop = 200;
        dispatch(messageActions.setIsRefreshed(false));
      } else {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }
  }, [selectedChatRoomId, messages.length]);

  const handleChatRoomClick = (roomId: string) => {
    dispatch(messageActions.setSelectedChatRoomId(roomId));
  };

  const handleSendMessage = async (text: string, imageUrl?: string) => {
    if (!selectedChatRoomId || (!text.trim() && !imageUrl) || !parent) return;

    const hasImage = !!imageUrl;
    const hasText = !!text.trim();
    const contentType = hasImage
      ? hasText
        ? MessageContentType.IMAGE_WITH_TEXT
        : MessageContentType.IMAGE
      : MessageContentType.TEXT;
    const content = hasImage
      ? hasText
        ? { url: imageUrl, text: text.trim() }
        : { url: imageUrl }
      : { text };

    const message = {
      user_uid: parent.uid,
      user_type: ChatRoomMemberType.PARENTS,
      message_type: MessageType.NORMAL,
      message_content_type: contentType,
      message_content: content,
      created_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      updated_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    };

    try {
      await sendChatRoomMessage({ roomId: selectedChatRoomId, message });
    } catch (error) {
      console.error("Failed to send message:", error);
      toast(
        <div>
          <p className="text text-textcolor-black">
            {i18n.t("MSG_TOAST_SEND_FAILED")}
          </p>
        </div>,
      );
    }
  };

  const handleRefresh = () =>
    new Promise<void>((resolve) => {
      if (!selectedChatRoomId || !chatRoomMessageSync) {
        resolve();
        return;
      }
      dispatch(messageActions.setIsRefreshed(true));
      dispatch(
        getParentChatRoomMessages({
          roomId: selectedChatRoomId,
          lastMessageSyncAt: chatRoomMessageSync.lastSyncAt,
          messageCount: 5,
          chatRoomType: selectedRoom?.type ?? ChatRoomType.TALK,
        }),
      )
        .then(unwrapResult)
        .then(() => resolve())
        .catch(() => resolve());
    });

  const handleClickRefresh = () => {
    if (!selectedChatRoomId || !chatRoomMessageSync) return;
    setRefresh(true);
    setTimeout(() => {
      dispatch(messageActions.setIsRefreshed(true));
      dispatch(
        getParentChatRoomMessages({
          roomId: selectedChatRoomId,
          lastMessageSyncAt: chatRoomMessageSync.lastSyncAt,
          messageCount: 5,
          chatRoomType: selectedRoom?.type ?? ChatRoomType.TALK,
        }),
      )
        .then(unwrapResult)
        .then(() => setRefresh(false))
        .catch(() => setRefresh(false));
    }, 500);
  };

  if (chatRooms.length === 0) {
    return (
      <div className="flex-col-el h-full items-center justify-center">
        <div className="text-[24px] text-gray2-100">
          {i18n.t("MSG_PARENT_PAGE_MESSAGE_NO_ROOMS")}
        </div>
      </div>
    );
  }

  const childUser = getChildUser(selectedRoom);

  return (
    <div className="flex-col-el h-full">
      <div className="grid grid-cols-[280px_1fr] gap-6 h-full">
        <div className="flex-col-el gap-3">
          {sortedParentChatRoom.map((room) => {
            const roomChildUser = getChildUser(room);
            const chatRoomSync = allParentChatRoomMessageSync.find(
              (sync) => sync.id === room.id,
            );
            const isNew =
              selectedChatRoomId !== room.id &&
              chatRoomSync &&
              (dayjs(
                chatRoomSync.latestMessageCreatedAt,
                "YYYY-MM-DDTHH:mm:ssZ",
              ).diff(dayjs(chatRoomSync.latestReadAt, "YYYY-MM-DDTHH:mm:ssZ")) >
                0 ||
                (chatRoomSync.latestReadAt === null &&
                  chatRoomSync.latestMessageCreatedAt !== null));

            return (
              <div
                key={room.id}
                className={`relative bg-white rounded-[16px] p-4 cursor-pointer transition-all border-2 ${
                  selectedChatRoomId === room.id
                    ? "bg-blue-60 border-blue-100"
                    : "border-transparent hover:bg-surface"
                }`}
                onClick={() => handleChatRoomClick(room.id)}
              >
                <div className="flex-row-el items-center gap-3 mb-2">
                  <UserAvatar size={64} icon={roomChildUser?.icon} />
                  <div className="flex-1">
                    <div className="text-[24px] text-textcolor-black">
                      {roomChildUser?.display_name || room.name}
                    </div>
                  </div>
                  {isNew && (
                    <div className="flex-row-center bg-red-80 w-[50px] h-[24px] text-white rounded-[4px]">
                      <p className="text text-[24px]! leading-[24px]!">New</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-[16px] flex flex-col h-full overflow-hidden">
          {selectedRoom && (
            <>
              <div className="flex-row-el items-center gap-3 py-5 px-6 border-b border-outline">
                <UserAvatar size={40} icon={childUser?.icon} />
                <div className="text-[27px] text-textcolor-black">
                  {childUser?.display_name || selectedRoom.name}
                </div>
              </div>

              <Collapse in={refresh} sx={{ width: "100%" }}>
                <div
                  className="flex-col-center w-full h-[80px]"
                  style={{ display: !refresh ? "none" : undefined }}
                >
                  <Spin />
                </div>
              </Collapse>

              <PullToRefresh
                className="flex-col-el h-full w-full bg-white"
                onRefresh={handleRefresh}
                pullingContent=""
                refreshingContent={<Spin />}
              >
                <div
                  ref={scrollRef}
                  className="h-full pt-2 flex-col-el overflow-y-auto"
                >
                  <button
                    className="bg-transparent border-none my-2 cursor-pointer"
                    onClick={handleClickRefresh}
                  >
                    <p className="text text-blue-100">
                      {i18n.t("MSG_PARENT_PAGE_MESSAGE_LOAD_MORE")}
                    </p>
                  </button>

                  {messages.map((message, index) => {
                    const isFromUser =
                      message.user_type === ChatRoomMemberType.PARENTS;
                    const isOfficial =
                      message.user_type === ChatRoomMemberType.OFFICIAL;
                    const showDate =
                      index === 0 ||
                      dayjs(
                        dayjs(
                          message.created_at,
                          "YYYY-MM-DDTHH:mm:ssZ",
                        ).format("YYYY-MM-DD"),
                      ).diff(
                        dayjs(
                          dayjs(
                            messages[index - 1].created_at,
                            "YYYY-MM-DDTHH:mm:ssZ",
                          ).format("YYYY-MM-DD"),
                        ),
                        "days",
                      ) > 0;

                    return (
                      <div key={message.id} className="flex-col-el relative">
                        {showDate && (
                          <MessageDate timestamp={message.created_at} />
                        )}
                        {isOfficial ? (
                          <OfficialChatMessageItem message={message} />
                        ) : (
                          <ChatMessageItem
                            isFromUser={isFromUser}
                            name={
                              isFromUser
                                ? parent?.display_name
                                : childUser?.display_name
                            }
                            icon={isFromUser ? parent?.icon : childUser?.icon}
                            messageContentType={message.message_content_type}
                            messageContent={message.message_content}
                            created_at={message.created_at}
                          />
                        )}
                      </div>
                    );
                  })}

                  {messages.length === 0 && (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-[21px] text-gray2-100">
                        {i18n.t("MSG_PARENT_PAGE_MESSAGE_NO_MESSAGES")}
                      </div>
                    </div>
                  )}
                </div>
              </PullToRefresh>

              <ParentChatInputBar
                onSend={handleSendMessage}
                disabled={!selectedChatRoomId}
                placeholder={i18n.t(
                  "MSG_PARENT_PAGE_MESSAGE_INPUT_PLACEHOLDER",
                )}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
